import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import moment from 'moment';
import Swal from 'sweetalert2';
import Tw_Cen_MT_Negrita from '../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import { NegocioConfig } from "../../../configuracion/Empresa";
import { formatoMoneda } from "../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 9,
        paddingTop: 35,
        paddingBottom: 45,
        paddingHorizontal: 40,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#2b589f',
        paddingBottom: 8,
    },
    companyInfoLeft: { flexGrow: 1, textAlign: 'left' },
    companyName: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 16, color: '#1f3c6d', marginBottom: 2 },
    companyAddress: { fontSize: 8, color: '#555', marginBottom: 1 },
    logoContainer: { width: 70, height: 70, marginLeft: 15 },
    logo: { width: 70, height: 70, objectFit: 'contain' },
    titleContainer: {
        marginTop: 5,
        marginBottom: 10,
        backgroundColor: '#f1f5f9',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 4,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reportTitle: {
        fontSize: 12,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#1f3c6d',
        textTransform: 'uppercase',
    },
    reportSubtitle: {
        fontSize: 8,
        color: '#64748b',
    },
    table: { width: '100%', marginTop: 5 },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1f3c6d',
        paddingVertical: 5,
        paddingHorizontal: 4,
        borderRadius: 2,
    },
    colHeader: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 8,
        color: '#ffffff',
        textAlign: 'left',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e2e8f0',
        paddingVertical: 4,
        paddingHorizontal: 4,
        alignItems: 'center',
    },
    tableRowEven: {
        backgroundColor: '#f8fafc',
    },
    colText: {
        fontSize: 8,
        color: '#334155',
    },
    colCode: { width: '15%' },
    colDesc: { width: '37%' },
    colCat: { width: '18%' },
    colIsv: { width: '10%', textAlign: 'center' },
    colPrice: { width: '12%', textAlign: 'right' },
    colStock: { width: '8%', textAlign: 'center' },

    summaryBox: {
        marginTop: 12,
        padding: 8,
        backgroundColor: '#f8fafc',
        borderRadius: 4,
        borderWidth: 0.5,
        borderColor: '#cbd5e1',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    summaryItem: {
        fontSize: 8,
        color: '#475569',
    },
    summaryValue: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#1f3c6d',
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 0.5,
        borderTopColor: '#cbd5e1',
        paddingTop: 5,
        fontSize: 7,
        color: '#94a3b8',
    },
});

export const GenerarReporteProductosPDF = async (productos = []) => {
    if (!productos || productos.length === 0) {
        Swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No hay productos para generar el reporte.'
        });
        return;
    }

    try {
        Swal.fire({
            title: 'Generando Catálogo PDF...',
            text: 'Por favor, espere mientras se compila el catálogo.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const blob = await pdf(<ReporteCatalogoProductos productos={productos} />).toBlob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        Swal.close();
    } catch (error) {
        Swal.close();
        console.error("Error al generar PDF de catálogo:", error);
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo generar el reporte PDF de productos.'
        });
    }
};

const ReporteCatalogoProductos = ({ productos = [] }) => {
    const totalProductos = productos.length;
    const totalExistencia = productos.reduce((acc, p) => acc + (Number(p.existencia || p.stock || 0)), 0);

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}>
                            <Image src={NegocioConfig.logo} style={styles.logo} />
                        </View>
                    )}
                </View>

                <View style={styles.titleContainer}>
                    <Text style={styles.reportTitle}>Catálogo General de Productos</Text>
                    <Text style={styles.reportSubtitle}>Fecha: {moment().format('DD/MM/YYYY HH:mm')}</Text>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.colHeader, styles.colCode]}>Código</Text>
                        <Text style={[styles.colHeader, styles.colDesc]}>Descripción</Text>
                        <Text style={[styles.colHeader, styles.colCat]}>Categoría</Text>
                        <Text style={[styles.colHeader, styles.colIsv]}>Impuesto</Text>
                        <Text style={[styles.colHeader, styles.colPrice]}>Precio</Text>
                        <Text style={[styles.colHeader, styles.colStock]}>Stock</Text>
                    </View>

                    {productos.map((prod, index) => {
                        const isEven = index % 2 === 0;
                        const precio = parseFloat(prod.price || prod.precio || 0);
                        const stock = Number(prod.existencia !== undefined ? prod.existencia : (prod.stock || 0));

                        return (
                            <View key={prod.id || index} style={[styles.tableRow, isEven ? styles.tableRowEven : {}]}>
                                <Text style={[styles.colText, styles.colCode]}>{prod.codigo || '—'}</Text>
                                <Text style={[styles.colText, styles.colDesc]}>{prod.nombre || prod.descripcion || 'Sin nombre'}</Text>
                                <Text style={[styles.colText, styles.colCat]}>{prod.TipoProducto?.nombre || prod.categoria || 'General'}</Text>
                                <Text style={[styles.colText, styles.colIsv]}>{prod.impuesto || 'Exento'}</Text>
                                <Text style={[styles.colText, styles.colPrice]}>{formatoMoneda(precio)}</Text>
                                <Text style={[styles.colText, styles.colStock]}>{stock}</Text>
                            </View>
                        );
                    })}
                </View>

                <View style={styles.summaryBox}>
                    <Text style={styles.summaryItem}>
                        Total de Productos Registrados: <Text style={styles.summaryValue}>{totalProductos}</Text>
                    </Text>
                    <Text style={styles.summaryItem}>
                        Unidades Totales en Existencia: <Text style={styles.summaryValue}>{totalExistencia}</Text>
                    </Text>
                </View>

                <View style={styles.footer} fixed>
                    <Text>{NegocioConfig.nombre} — Documento de Control Interno</Text>
                    <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
                </View>
            </Page>
        </Document>
    );
};

export default ReporteCatalogoProductos;
